import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, ClipboardList, Loader2, LockKeyhole } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LampScene } from "@/components/login/LampScene";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import "./login-lamp.css";

export default function Login() {
  const { isAuthed, login } = useAuth();
  const navigate = useNavigate();
  const usernameRef = useRef(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [lampOn, setLampOn] = useState(false);

  useEffect(() => {
    if (!lampOn) return undefined;
    const timer = window.setTimeout(() => usernameRef.current?.focus(), 620);
    return () => window.clearTimeout(timer);
  }, [lampOn]);

  if (isAuthed) return <Navigate to="/" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid username or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className={`lamp-login-room ${lampOn ? "lamp-is-on" : ""}`} data-testid="login-page">
      <div className="lamp-room-lines" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid min-h-[100svh] w-full max-w-6xl grid-cols-1 items-center gap-2 px-4 pb-14 pt-6 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:py-12">
        <section className="flex min-h-[300px] items-center justify-center lg:col-span-5 lg:min-h-[600px]"
          aria-label="Interactive lamp switch">
          <LampScene isOn={lampOn} onToggle={() => setLampOn((current) => !current)} />
        </section>

        <section className="flex w-full items-center justify-center px-1 sm:px-6 lg:col-span-7 lg:justify-start"
          aria-hidden={!lampOn}>
          <div className="lamp-login-card w-full max-w-md" data-testid="login-card">
            <div className="lamp-card-sheen" aria-hidden="true" />
            <div className="relative">
              <div className="mb-8 flex items-start justify-between gap-5">
                <div>
                  <div className="mb-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-300/90"
                    data-testid="login-access-badge">
                    <LockKeyhole className="h-3.5 w-3.5" /> Secure access
                  </div>
                  <h1 className="text-2xl font-extrabold text-amber-50 sm:text-3xl" data-testid="login-title">
                    Operations Suite
                  </h1>
                  <p className="mt-2 text-sm font-medium text-slate-400" data-testid="login-subtitle">
                    Sign in to continue
                  </p>
                </div>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-amber-300/25 bg-amber-400/10 text-amber-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
                  <ClipboardList className="h-6 w-6" />
                </div>
              </div>

              <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-username" className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Username
                  </Label>
                  <Input ref={usernameRef} id="login-username" name="username" value={username}
                    onChange={(event) => setUsername(event.target.value)} data-testid="login-username"
                    placeholder="Username" autoComplete="username" tabIndex={lampOn ? 0 : -1}
                    className="lamp-login-input h-12 border-white/15 bg-slate-950/60 px-4 text-slate-100 placeholder:text-slate-500" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Password
                  </Label>
                  <Input id="login-password" name="password" type="password" value={password}
                    onChange={(event) => setPassword(event.target.value)} data-testid="login-password"
                    placeholder="Password" autoComplete="current-password" tabIndex={lampOn ? 0 : -1}
                    className="lamp-login-input h-12 border-white/15 bg-slate-950/60 px-4 text-slate-100 placeholder:text-slate-500" />
                </div>

                {error && (
                  <p role="alert" className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-200"
                    data-testid="login-error">{error}</p>
                )}

                <Button type="submit" disabled={busy} tabIndex={lampOn ? 0 : -1} data-testid="login-submit"
                  className="lamp-login-submit group h-12 w-full text-base font-bold text-slate-950">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {busy ? "Signing in…" : "Sign In"}
                  {!busy && <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </div>

      <p className="absolute inset-x-4 bottom-4 z-20 text-center text-xs text-slate-500/80" data-testid="login-support-message">
        If you encounter any problems, please provide feedback to Candy.
      </p>
    </main>
  );
}